<template>
  <form-section :title="title">
    <template slot="controls">
      <content-actions
        v-model="value"
        :channel="$route.query.channel"
        :is-content-empty="isContentEmpty"
        import-enabled
        export-enabled
        apply-template-enabled
        copy-enabled
        :model="model"
      />

      <slot name="selected-template-info"></slot>
    </template>
    <el-row>
      <el-col :lg="10" :xl="8">
        <!-- Message title -->
        <form-item
          prop="push_title"
          :label="$t('CampaignForm_Push_Field_Subject')"
        >
          <snippet-input
            id="push_title"
            v-model="value.push_title"
            :optimove="isOptimove"
            :salesforce="isSalesforce"
            data-qa="web-push-title-input"
          />
        </form-item>

        <!-- Message Text -->
        <form-item prop="push_text" :label="$t('CampaignForm_Push_Field_Text')">
          <snippet-input
            type="textarea"
            v-model="value.push_text"
            :optimove="isOptimove"
            :salesforce="isSalesforce"
            data-qa="web-push-message-input"
          />
        </form-item>

        <!-- Voucher code settings -->
        <template v-if="model.voucher_codes_enabled">
          <form-item
            prop="voucher_code_enabled"
            :label="$t('CampaignForm_VoucherType_Title')"
          >
            <enum-select
              v-model="value.voucher_code_enabled"
              :options="voucherTypesLabels"
              :number="true"
              data-qa="voucher_code_enabled"
            />
          </form-item>

          <form-item
            prop="voucher_code_position"
            :label="$t('CampaignForm_VoucherPosition_Title')"
            v-if="value.voucher_code_enabled === 1"
          >
            <enum-select
              v-model="value.voucher_code_position"
              :options="voucherPositionsLabels"
              :number="true"
              data-qa="voucher_code_position"
            />
          </form-item>
        </template>

        <!-- URL -->
        <form-item :label="$t('CampaignForm_Push_Action_Url')">
          <el-input
            v-model="value.push_action.url"
            data-qa="web-push-url-input"
          />
        </form-item>

        <!-- Icon -->
        <form-item
          prop="push_icon"
          :label="$t('CampaignForm_UploadIcon')"
          class="push-icon-ct"
        >
          <campaign-image-upload
            :value="value.push_icon"
            :file.sync="value.push_icon"
            :message-type="channelTypes.web_push"
            upload-type="icon"
            size="small"
          />
        </form-item>

        <form-item
          prop="push_picture"
          :label="$t('CampaignForm_UploadType_Upload')"
          class="push-icon-ct"
        >
          <campaign-image-upload
            :value="value.push_picture"
            :file.sync="value.push_picture"
            :message-type="channelTypes.web_push"
            upload-type="picture"
          />
        </form-item>

        <!-- Push Category -->
        <form-item
          prop="push_category_id"
          :label="$t('CampaignForm_Push_Category')"
        >
          <el-autocomplete
            :placeholder="$t('CampaignForm_Select')"
            clearable
            v-model="category"
            :fetch-suggestions="querySearchCategory"
            @select="handleSelectCategory"
            @clear="clearCategorySelection"
            data-qa="push-category-input"
          />
        </form-item>

        <template v-for="(button, index) in buttons">
          <form-item
            v-if="button.action !== ''"
            :key="index"
            :label="
              $t('CampaignForm_Push_ActionType', {
                action:
                  button.action.charAt(0).toUpperCase() +
                  button.action.slice(1),
                title: button.title
              })
            "
          >
            <el-input
              v-model="value.push_buttons[button.id][button.action]"
              data-qa="push-category-option"
            />
          </form-item>
        </template>
      </el-col>

      <!-- Preview -->
      <el-col :lg="14" :xl="16" class="preview">
        <el-select v-model="selectedOs" class="os-selector">
          <el-option value="macos" label="MacOS" />
          <el-option value="windows" label="Windows" />
          <el-option value="android" label="Android" />
        </el-select>

        <div class="push-preview-ct">
          <!-- Android -->
          <template v-if="selectedOs === 'android'">
            <img src="@/img/browsers/chrome.png" />
            <push-preview-web-android
              browser="chrome"
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
              :picture="value.push_picture"
              show-picture-on-hover
              :actions="androidActions"
            />

            <img src="@/img/browsers/firefox.png" />
            <push-preview-web-android
              browser="firefox"
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
              :actions="androidActions"
            />
          </template>

          <!-- MacOs -->
          <template v-if="selectedOs === 'macos'">
            <img src="@/img/browsers/safari.png" />
            <push-preview-web-mac-safari
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
            />

            <img src="@/img/browsers/chrome.png" />
            <push-preview-web-mac-chrome
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
            />

            <img src="@/img/browsers/firefox.png" />
            <push-preview-web-mac-firefox
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
            />
          </template>

          <!-- Windows -->
          <template v-if="selectedOs === 'windows'">
            <img src="@/img/browsers/chrome.png" />
            <push-preview-web-windows-chrome
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
              :picture="value.push_picture"
              :actions="actions"
            />

            <img src="@/img/browsers/firefox.png" />
            <push-preview-web-windows-firefox
              :title="value.push_title"
              :icon="value.push_icon"
              :message="previewPushText"
              :picture="value.push_picture"
            />
          </template>
        </div>
      </el-col>
    </el-row>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import { EventBus } from '@/core/EventBus'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import PushPreviewWebMacSafari from '@/common/components/push/PushPreviewWebMacSafari'
import PushPreviewWebMacChrome from '@/common/components/push/PushPreviewWebMacChrome'
import PushPreviewWebMacFirefox from '@/common/components/push/PushPreviewWebMacFirefox'
import PushPreviewWebAndroid from '@/common/components/push/PushPreviewWebAndroid'
import PushPreviewWebWindowsFirefox from '@/common/components/push/PushPreviewWebWindowsFirefox'
import PushPreviewWebWindowsChrome from '@/common/components/push/PushPreviewWebWindowsChrome'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import SnippetInput from '@/modules/campaign-manage/components/SnippetInput'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import CampaignImageUpload from '@/modules/campaign-manage/components/content/CampaignImageUpload'
import ContentActions from '@/modules/campaign-manage/components/ContentActions'
import { voucherTypesLabels } from '@/modules/campaign-manage/enum/VoucherTypesEnum'
import {
  voucherPositionsLabels,
  voucherPositions
} from '@/modules/campaign-manage/enum/VoucherPositionsEnum'
import EnumSelect from '@/common/components/form/EnumSelect'
import _ from 'lodash'

export default {
  name: 'WebPush',

  components: {
    ContentActions,
    CampaignImageUpload,
    SnippetInput,
    PushPreviewWebWindowsChrome,
    PushPreviewWebWindowsFirefox,
    PushPreviewWebAndroid,
    PushPreviewWebMacFirefox,
    PushPreviewWebMacChrome,
    PushPreviewWebMacSafari,
    FormSection,
    FormItem,
    EnumSelect
  },

  props: {
    value: {
      required: true
    },
    title: {
      required: true
    },
    model: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      channelTypes,
      voucherPositionsLabels,
      voucherPositions,
      voucherTypesLabels,
      selectedOs: 'macos',
      category: '',
      buttons: []
    }
  },

  mounted() {
    EventBus.$on('applyPushTemplate-' + this.key, data => {
      // reset props to default
      this.resetChannelProps()
      this.resetLocalProps()

      // set template props
      for (const [key, value] of Object.entries(data)) {
        if (key === 'push_buttons' && value === null) {
          continue
        }

        this.value[key] = value
      }
      if (
        this.value.push_action &&
        this.value.push_action.hasOwnProperty('action')
      ) {
        this.defaultAction = this.value.push_action.action
      }
      if (this.value.push_category_id) {
        this.fetchCategory(this.value.push_category_id)
        this.isPayloadBtnDisabled()
      }
    })

    if (this.value.push_category_id) {
      this.fetchCategory(this.value.push_category_id)
    }
  },

  computed: {
    isContentEmpty() {
      return !this.value.push_title && !this.value.push_text
    },
    isOptimove() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE
    },
    isSalesforce() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_SALESFORCE
    },
    key() {
      return `${this.$route.query.channel}_${this.$route.query.variant}_${this.$route.query.lang}`
    },
    lang() {
      if (this.$route.query.lang === 'default') {
        return 'en'
      }
      return this.$route.query.lang
    },
    actions() {
      return this.buttons.map(btn => {
        if (btn.hasOwnProperty('title_languages')) {
          return btn.title_languages[this.lang]
        } else {
          return btn.title
        }
      })
    },
    androidActions() {
      let actions = _.clone(this.actions)
      if (actions && actions.length) {
        actions.push(this.$t('NotificationPreview_Android_Settings_Btn'))
      }
      return actions
    },
    previewPushText() {
      let text = this.value.push_text
      if (this.model.voucher_codes_enabled && this.value.voucher_code_enabled) {
        if (text === '') return this.$t('CampaignForm_VoucherCode_Sample')
        if (this.value.voucher_code_position === voucherPositions.TOP) {
          text = this.$t('CampaignForm_VoucherCode_Sample') + '\n' + text
        } else {
          text = text + '\n' + this.$t('CampaignForm_VoucherCode_Sample')
        }
      }
      return text
    }
  },

  methods: {
    resetChannelProps() {
      this.value.push_category_id = null
      this.value.push_title = null
      this.value.push_text = null
      this.value.push_icon = null
      this.value.push_picture = null
      this.value.push_action = {
        action: 'url',
        url: ''
      }
      this.value.push_buttons = {}
    },

    resetLocalProps() {
      this.category = ''
      this.buttons = []
    },

    removeIcon() {
      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.value.push_icon = null
        })
        .catch(() => {})
    },

    removePicture() {
      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.value.push_picture = null
        })
        .catch(() => {})
    },
    querySearchCategory(queryString, cb) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'push-categories',
          params: {
            title: queryString,
            type: 'web'
          }
        })
      ).then(({ data }) => {
        const list = data.map(item => {
          return {
            id: item.id,
            value: item.title
          }
        })
        cb(list)
      })
    },

    handleSelectCategory(category) {
      this.value.push_category_id = category.id
      this.fetchCategory(category.id)
      if (
        Array.isArray(this.value.push_buttons) &&
        this.value.push_buttons.length === 0
      ) {
        this.value.push_buttons = {}
      }
    },

    clearCategorySelection() {
      this.buttons = []
      this.value.push_buttons = {}
      this.value.push_category_id = null
    },

    fetchCategory(categoryId) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'push-categories',
          params: {
            id: categoryId
          }
        })
      ).then(({ data }) => {
        this.category = data[0].title
        this.buttons = []

        const buttons = data[0].buttons || []

        buttons.forEach(button => {
          if (!this.value.push_buttons.hasOwnProperty(button.id)) {
            this.$set(this.value.push_buttons, button.id, {
              [button.action]: ''
            })
          }
        })

        if (data[0].icon && !this.value.push_icon) {
          this.value.push_icon = data[0].icon
        }

        this.buttons = buttons
      })
    }
  }
}
</script>

<style scoped>
.push-icon-ct >>> .el-form-item__content {
  display: flex;
}

.icon-ct {
  align-self: center;
  margin-left: 10px;
}

.icon {
  font-size: 18px;
  color: #a6a6a6;
}

.icon:hover {
  cursor: pointer;
}

.dropdown-btn {
  padding: 8px;
}

.rowCt {
  display: flex;
  padding-bottom: 20px;
}

.rowCt div:not(:last-child) {
  padding-right: 10px;
}

.rowCt .icon {
  display: flex;
  justify-content: center;
  width: 25px;
  height: 40px;
  color: #a7acba;
}

.rowCt .icon:hover {
  cursor: pointer;
}

.preview {
  background-color: #f8fafc;
  border-radius: 4px;
  min-height: 610px;
  padding: 20px !important;
  position: relative;
  display: flex;
  flex-direction: column;
}

.os-selector {
  width: 50%;
  max-width: 250px;
  margin-bottom: 20px;
  align-self: flex-end;
}

.push-preview-ct {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.push-preview-ct > div {
  margin-bottom: 40px;
}

.push-preview-ct > img {
  width: 40px;
  height: 40px;
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/WebPush.vue